export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  readonly retryAfterSeconds?: number;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
    retryAfterSeconds?: number
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  csrfToken?: string;
  readToken?: string;
}

export async function apiRequest<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }
  if (options.csrfToken) headers.set("X-CSRF-Token", options.csrfToken);
  if (options.readToken) headers.set("X-Read-Token", options.readToken);

  let response: Response;
  try {
    response = await fetch(path, {
      ...options,
      headers,
      body,
      credentials: "same-origin",
      cache: "no-store"
    });
  } catch {
    throw new ApiError(0, "offline", "无法连接服务器，请检查网络后重试");
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  const payload: unknown = contentType.includes("application/json") ? await response.json() : null;
  if (!response.ok) {
    const error: Record<string, unknown> =
      payload &&
      typeof payload === "object" &&
      "error" in payload &&
      payload.error &&
      typeof payload.error === "object"
        ? (payload.error as Record<string, unknown>)
        : {};
    throw new ApiError(
      response.status,
      typeof error.code === "string" ? error.code : "request_failed",
      typeof error.message === "string" ? error.message : "请求失败",
      error.details,
      Number(response.headers.get("Retry-After")) || undefined
    );
  }
  return payload as T;
}
